import { formatValidQueryParamUrlCommaSeparated } from "utils/common";
import {
  formatEventsResponse,
  formatEventsSearchResponse
} from "utils/events.util";

import { emptySplitApi } from "./empty-split-api";
import {
  UNSCOPED_WINDOW_ID,
  eventsWindowTags
} from "./events-window-tags.util";
import {
  type EventFilters,
  type EventSearchOption,
  type Events,
  type EventsResponse
} from "./types/events.type";

export const EMPTY_STAFF_ID = -1;

/** The grid's poll interval, in seconds. A cached window younger than this is current. */
export const EVENTS_POLL_SECONDS = 600;

/**
 * How long a window nobody is viewing stays cached (ENG-2225). Long enough to cover going back
 * and forth between weeks, short enough to bound how stale a revisited week can be (other
 * users' creates and moves only arrive by polling) and how many payloads stay in memory.
 */
export const EVENTS_WINDOW_KEEP_SECONDS = 180;

const getEventsDefinition = {
  query: (filters: EventFilters) =>
    `/events-optimized/?${formatValidQueryParamUrlCommaSeparated({
      ...filters.baseFilter,
      from_date: filters.fromDate,
      to_date: filters.toDate,
      query: filters.query,
      clinic: filters.clinicIds,
      staff_member: filters.staffIds,
      type: filters.eventType,
      search: filters.search && encodeURIComponent(filters.search),
      assigned_tags: filters.assigned_tags
    })}`,
  // Per-day tags (ENG-2225). Type-level invalidateTags(["Events"]) still matches every one of
  // them, so only callers that invalidate by id see a difference.
  providesTags: (
    _result: Events | undefined,
    _error: unknown,
    filters: EventFilters
  ) => eventsWindowTags(filters),
  transformResponse: (response: EventsResponse) =>
    formatEventsResponse(response)
};

export const eventsApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getEvents: builder.query<Events, EventFilters>(getEventsDefinition),
    // ENG-2225 (flag schedulerEventsRefetchScoping): the same query, kept for 3 minutes after
    // its last subscriber leaves instead of the 60s default, so going back to a week just seen
    // is served from cache. A separate endpoint because RTK sets keepUnusedDataFor per
    // endpoint, not per call; the flag picks one in useSchedulerEvents.
    getEventsWindow: builder.query<Events, EventFilters>({
      ...getEventsDefinition,
      keepUnusedDataFor: EVENTS_WINDOW_KEEP_SECONDS
    }),
    searchEvents: builder.query<EventSearchOption[], EventFilters>({
      query: (filters) =>
        `/events-optimized/?${formatValidQueryParamUrlCommaSeparated({
          ...filters.baseFilter,
          from_date: filters.fromDate,
          to_date: filters.toDate,
          query: filters.query,
          clinic: filters.clinicIds,
          staff_member: filters.staffIds,
          type: filters.eventType,
          search: filters.search && encodeURIComponent(filters.search),
          ordering: filters.ordering,
          limit: filters.limit
        })}`,
      // A search spans an arbitrary range, so it refreshes on every scoped invalidation.
      providesTags: [{ type: "Events", id: UNSCOPED_WINDOW_ID }],
      transformResponse: (response: EventsResponse) => {
        return formatEventsSearchResponse(response);
      }
    })
  })
});

export const {
  useGetEventsQuery,
  useGetEventsWindowQuery,
  useSearchEventsQuery
} = eventsApi;
